import { Plus } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useMatch, useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { InvitePanel } from '../invitations/InvitePanel';
import { usePlaceContext, type PlaceContext } from '../place/PlaceLayout';
import { apiPlace, sectionPath } from '../place/place';
import { PageHead } from '../ui/PageHead';
import { PhonePrimary } from '../ui/PhonePrimary';
import { PeopleListPane } from './PeopleListPane';
import { ALL, PeopleToolbar, STATUSES } from './PeopleToolbar';
import { useAccessActions } from './person-actions';
import type { SheetClosing } from './PersonSheet';
import { itemKey, usePeople, type PeopleFilters, type PeopleItem } from './use-people';

export { personMenu } from './PeopleRows';

function readFilters(params: URLSearchParams): PeopleFilters {
  const status = params.get('status');
  return {
    q: params.get('q') ?? '',
    roleId: params.get('role'),
    status: STATUSES.find((value) => value === status) ?? null,
    sort: params.get('sort') === 'updated' ? 'updated' : 'name',
  };
}

/** What Utenti hands its child route, the person's sheet. */
export type PeopleContext = PlaceContext & { readonly reloadPeople: () => void };

type SetParams = ReturnType<typeof useSearchParams>[1];

/** The quick search's «Invita persona» lands here with ?invite=1: open the panel, once. */
function useOpenInviteFromLink(
  params: URLSearchParams,
  setParams: SetParams,
  canInvite: boolean,
  setInviting: (open: boolean) => void,
) {
  useEffect(() => {
    if (params.get('invite') === '1' && canInvite) {
      setInviting(true);
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete('invite');
          return next;
        },
        { replace: true },
      );
    }
  }, [params, setParams, canInvite]);
}

function useFilterSetters(setParams: SetParams) {
  const setFilter = useCallback(
    (key: 'q' | 'role' | 'status' | 'sort', value: string | null) =>
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (value === null || value === '' || value === ALL) {
            next.delete(key);
          } else {
            next.set(key, value);
          }
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );
  // «Azzera i filtri» clears what narrows the list; the order is not a filter, and stays.
  const clearFilters = useCallback(
    () =>
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const key of ['q', 'role', 'status']) {
            next.delete(key);
          }
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );
  return { setFilter, clearFilters };
}

/**
 * A sheet that closed (Esc, ×, «Torna agli utenti») gives the focus back to its person's row,
 * once per closing, so a keyboard user carries on from where they were.
 */
function useFocusClosedSheet(sheetOpen: boolean, items: readonly PeopleItem[]) {
  const location = useLocation();
  const focusPerson = (location.state as SheetClosing | null)?.focusPerson;
  const focusedFor = useRef<string | null>(null);
  useEffect(() => {
    if (sheetOpen || focusPerson === undefined || focusedFor.current === location.key) {
      return;
    }
    const row = Array.from(document.querySelectorAll<HTMLElement>('[data-person-link]')).find(
      (link) => link.dataset.personLink === focusPerson,
    );
    if (row !== undefined) {
      row.focus();
      focusedFor.current = location.key;
    }
  }, [sheetOpen, focusPerson, location.key, items]);
}

function PhoneInvite({ onInvite }: { readonly onInvite: () => void }) {
  const { t } = useTranslation('administration');
  return (
    <PhonePrimary>
      <Button onClick={onInvite}>
        <Plus />
        {t('people.inviteShort')}
      </Button>
    </PhonePrimary>
  );
}

function InviteControls({
  canInvite,
  inviting,
  setInviting,
  placeKind,
  placeId,
  placeName,
  reload,
}: {
  readonly canInvite: boolean;
  readonly inviting: boolean;
  readonly setInviting: (open: boolean) => void;
  readonly placeKind: 'tenants' | 'organizations';
  readonly placeId: string;
  readonly placeName: string;
  readonly reload: () => void;
}) {
  if (!canInvite) return null;
  return (
    <>
      <PhoneInvite onInvite={() => setInviting(true)} />
      <InvitePanel
        open={inviting}
        onOpenChange={setInviting}
        placeKind={placeKind}
        placeId={placeId}
        placeName={placeName}
        onInvited={reload}
      />
    </>
  );
}

function PeopleHead({
  titleId,
  placeName,
  canInvite,
  onInvite,
  toolbar,
}: {
  readonly titleId: string;
  readonly placeName: string;
  readonly canInvite: boolean;
  readonly onInvite: () => void;
  readonly toolbar: ReactNode;
}) {
  const { t } = useTranslation('administration');
  return (
    <div className="people-head">
      <PageHead
        titleId={titleId}
        title={t('people.title')}
        summary={t('people.summary', { place: placeName })}
        actions={
          canInvite ? (
            <span className="page-primary">
              <Button onClick={onInvite}>
                <Plus />
                {t('people.invite')}
              </Button>
            </span>
          ) : undefined
        }
      />
      {toolbar}
    </div>
  );
}

/**
 * Utenti (FR-AD-4): the people of the place with their roles here, and its pending invitations, in
 * one list. The filters live in the address, so a filtered list can be linked, reloaded and come
 * back to with the back button. An invitation's row is built only from the invitation, so the list
 * never reveals whether an address belongs to someone who already exists.
 *
 * The person's sheet is a child route: beside the list from a 900 px content width, and the whole
 * page below it (the approved boards; the switch is a container query in shell.css).
 */
export function PeoplePage() {
  const context = usePlaceContext();
  const { place, detail } = context;
  const pair = apiPlace(place);
  const placeKind = pair?.placeKind ?? 'organizations';
  const placeId = pair?.placeId ?? '';
  const placeName = detail.place.name;
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => readFilters(params), [params]);
  const { state, more, reload, hide, restore, hidden } = usePeople(placeKind, placeId, filters);
  const listPath = sectionPath(place, 'people');
  const query = params.size > 0 ? `?${params.toString()}` : '';
  const sheetOpen = useMatch(`${listPath}/:userId`) !== null;
  const [inviting, setInviting] = useState(false);
  useOpenInviteFromLink(params, setParams, detail.can.invite, setInviting);
  const titleId = useId();

  const { setFilter, clearFilters } = useFilterSetters(setParams);

  const actions = useAccessActions({
    placeKind,
    placeId,
    placeName,
    onChanged: reload,
    onStanding: context.refreshPlace,
  });
  const filtered = filters.q !== '' || filters.roleId !== null || filters.status !== null;
  const items = state.items.filter((item) => !hidden.has(itemKey(item)));
  const childContext: PeopleContext = { ...context, reloadPeople: reload };

  useFocusClosedSheet(sheetOpen, items);

  return (
    <section
      aria-labelledby={titleId}
      className={cn('people-section', sheetOpen && 'people-section-with-sheet')}
    >
      <PeopleHead
        titleId={titleId}
        placeName={placeName}
        canInvite={detail.can.invite}
        onInvite={() => setInviting(true)}
        toolbar={
          <PeopleToolbar
            filters={filters}
            setFilter={setFilter}
            filtered={filtered}
            onClear={clearFilters}
            placeKind={placeKind}
            placeId={placeId}
          />
        }
      />
      <div className={cn('people-layout', sheetOpen && 'people-layout-with-sheet')}>
        <div className="people-list-pane">
          <PeopleListPane
            state={state}
            items={items}
            more={more}
            reload={reload}
            filtered={filtered}
            placeName={placeName}
            actions={actions}
            onClear={clearFilters}
            personHref={(id) => `${listPath}/${encodeURIComponent(id)}${query}`}
            onHide={hide}
            onRestore={restore}
          />
        </div>
        <Outlet context={childContext} />
      </div>
      <InviteControls
        canInvite={detail.can.invite}
        inviting={inviting}
        setInviting={setInviting}
        placeKind={placeKind}
        placeId={placeId}
        placeName={placeName}
        reload={reload}
      />
    </section>
  );
}
